import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  writeBatch,
  increment,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebase";
import {
  Campanha,
  Disparo,
  StatusCampanha,
  StatusDisparo,
} from "@/types/campanha";
import { Participante, Confirmado } from "@/types/participante";

const COL = "campanhas";

// ─── Upload de mídia ─────────────────────────────────────────────────────────

export async function uploadMidia(file: File, campanhaId: string): Promise<string> {
  const ext = file.name.split(".").pop();
  const storageRef = ref(storage, `campanhas/${campanhaId}/${Date.now()}.${ext}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

// ─── Campanhas ───────────────────────────────────────────────────────────────

export async function criarCampanha(dados: {
  nome: string;
  edicaoId: string;
  edicaoNome: string;
  fonte: Campanha["fonte"];
  tipoMensagem: Campanha["tipoMensagem"];
  mensagem: string;
  midiaUrl?: string;
  contatos: (Participante | Confirmado)[];
}): Promise<string> {
  const { contatos, ...campanhaDados } = dados;

  const { midiaUrl, ...restoCampanha } = campanhaDados;
  const ref = await addDoc(collection(db, COL), {
    ...restoCampanha,
    ...(midiaUrl !== undefined ? { midiaUrl } : {}),
    status: "rascunho" as StatusCampanha,
    totalContatos: contatos.length,
    enviados: 0,
    falhas: 0,
    criadoEm: serverTimestamp(),
  });

  // Cria os disparos em lotes de 400
  const LOTE = 400;
  for (let i = 0; i < contatos.length; i += LOTE) {
    const batch = writeBatch(db);
    contatos.slice(i, i + LOTE).forEach((c) => {
      const disparoRef = doc(collection(db, COL, ref.id, "disparos"));
      batch.set(disparoRef, {
        participanteId: c.id,
        nome: c.nomeCompleto,
        whatsapp: c.whatsapp,
        status: "pendente" as StatusDisparo,
      });
    });
    await batch.commit();
  }

  return ref.id;
}

export async function listarCampanhas(): Promise<Campanha[]> {
  const q = query(collection(db, COL), orderBy("criadoEm", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(docToCampanha);
}

export async function buscarCampanha(id: string): Promise<Campanha | null> {
  const snap = await getDoc(doc(db, COL, id));
  if (!snap.exists()) return null;
  return docToCampanha(snap);
}

export function ouvirCampanha(
  id: string,
  cb: (campanha: Campanha | null) => void
) {
  return onSnapshot(doc(db, COL, id), (snap) => {
    cb(snap.exists() ? docToCampanha(snap) : null);
  });
}

export async function atualizarStatusCampanha(
  id: string,
  status: StatusCampanha,
  extra?: Partial<Record<string, unknown>>
): Promise<void> {
  await updateDoc(doc(db, COL, id), { status, ...extra });
}

export async function deletarCampanha(id: string): Promise<void> {
  await deleteDoc(doc(db, COL, id));
}

// ─── Disparos ────────────────────────────────────────────────────────────────

export async function listarDisparos(campanhaId: string): Promise<Disparo[]> {
  const q = query(
    collection(db, COL, campanhaId, "disparos"),
    orderBy("nome", "asc")
  );
  const snap = await getDocs(q);
  return snap.docs.map(docToDisparo);
}

export function ouvirDisparos(
  campanhaId: string,
  cb: (disparos: Disparo[]) => void
) {
  const q = query(
    collection(db, COL, campanhaId, "disparos"),
    orderBy("nome", "asc")
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map(docToDisparo)));
}

export async function atualizarDisparo(
  campanhaId: string,
  disparoId: string,
  dados: { status: StatusDisparo; erro?: string; enviadoEm?: Date }
): Promise<void> {
  await updateDoc(doc(db, COL, campanhaId, "disparos", disparoId), {
    ...dados,
    ...(dados.enviadoEm ? { enviadoEm: Timestamp.fromDate(dados.enviadoEm) } : {}),
  });
}

export async function incrementarContador(
  campanhaId: string,
  campo: "enviados" | "falhas"
): Promise<void> {
  await updateDoc(doc(db, COL, campanhaId), { [campo]: increment(1) });
}

export async function resetarFalhas(campanhaId: string): Promise<void> {
  const q = query(collection(db, COL, campanhaId, "disparos"));
  const snap = await getDocs(q);
  const falhos = snap.docs.filter((d) => d.data().status === "falha");

  const LOTE = 400;
  for (let i = 0; i < falhos.length; i += LOTE) {
    const batch = writeBatch(db);
    falhos.slice(i, i + LOTE).forEach((d) => {
      batch.update(d.ref, { status: "pendente", erro: null });
    });
    await batch.commit();
  }

  // Desconta as falhas do contador
  await updateDoc(doc(db, COL, campanhaId), {
    falhas: 0,
    status: "rascunho" as StatusCampanha,
  });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function docToCampanha(d: any): Campanha {
  const data = d.data();
  return {
    id: d.id,
    nome: data.nome,
    edicaoId: data.edicaoId,
    edicaoNome: data.edicaoNome,
    fonte: data.fonte,
    tipoMensagem: data.tipoMensagem,
    mensagem: data.mensagem,
    midiaUrl: data.midiaUrl ?? undefined,
    status: data.status,
    totalContatos: data.totalContatos ?? 0,
    enviados: data.enviados ?? 0,
    falhas: data.falhas ?? 0,
    criadoEm: (data.criadoEm as Timestamp)?.toDate() ?? new Date(),
    iniciadoEm: (data.iniciadoEm as Timestamp)?.toDate() ?? undefined,
    concluidoEm: (data.concluidoEm as Timestamp)?.toDate() ?? undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function docToDisparo(d: any): Disparo {
  const data = d.data();
  return {
    id: d.id,
    participanteId: data.participanteId,
    nome: data.nome,
    whatsapp: data.whatsapp,
    status: data.status,
    erro: data.erro ?? undefined,
    enviadoEm: (data.enviadoEm as Timestamp)?.toDate() ?? undefined,
  };
}
